import {
  fixLogoAltText,
  fixSliderForFewSlides,
  hideOnEmpty,
  installLogoOrganizationInterceptor,
  stripCountryInLocationStrings,
} from '../../../javascripts/utils/jobiqo';

// Install the fetch interceptor at module load time — before jobiqoJobWidget.init()
// is ever called — so the logo→company map is ready when the widget renders cards.
const logoOrganizationMap = installLogoOrganizationInterceptor();

const configEl = document.querySelector<HTMLScriptElement>('[data-jobiqo-recommended-jobs-config]');

if (configEl) {
  const config = JSON.parse(configEl.textContent ?? '{}');

  const initWidget = (): void => {
    if (typeof jobiqoJobWidget === 'undefined') {
      return;
    }

    const filters: Record<string, unknown> = {};

    // Life phase → filters.taxonomy (AND-combined with other filters by the widget)
    if (config.filter.lifePhaseCondition) {
      filters.taxonomy = [{
        operator: '=',
        name: config.filter.lifePhaseCondition.field,
        value: config.filter.lifePhaseCondition.id,
      }];
    }

    // All personalized OR conditions: occupational fields + industry IDs merged into one OR pool.
    // Approach: flat OR — "show jobs where occupationalField=X OR industry=Y OR ..."
    const occupationalFields: string[] = config.filter.occupationalFields ?? [];
    const industryIds: string[] = config.filter.industryIds ?? [];

    const orConditions: Array<{ operator: '='; name: string; value: string }> = [
      ...occupationalFields.map((id: string) => ({
        operator: '=' as const,
        name: 'field_job_occupational_field_parents_all',
        value: id,
      })),
      ...industryIds.map((id: string) => ({
        operator: '=' as const,
        name: 'job_industry',
        value: id,
      })),
    ];

    if (orConditions.length > 0) {
      filters.extraConditions = {
        conjunction: 'OR',
        conditions: orConditions,
      };
    }

    const widgetConfig = {
      // Falls back to location.origin when env vars are empty (reverse-proxy mode)
      backendDomain: config.backendDomain ?? window.location.origin,
      baseUrl: config.baseUrl ?? window.location.origin,
      showSearchBar: false,
      showSearchResult: true,
      slider: true,
      slidesToShow: 3,
      // Declared explicitly so that the step from 1 slide per view to 2 happens at the same
      // 767/768 boundary the data-few-slides rules in jobiqo-jobs-cards.scss use. The widget's
      // own default ladder keeps 1 slide per view up to and including 768px, which would leave
      // a 2-job slider scrollable at 768px while the stylesheet already hides the cloned slides
      // Slick needs for the track offset.
      slidesResponsive: [
        { breakpoint: 1024, settings: { slidesToShow: 2 } },
        { breakpoint: 767, settings: { slidesToShow: 1 } },
      ],
      utmParameters: 'false', // disable default utm params
      jobAmount: config.limit ?? 10,
      filters,
      styles: {
        fontFamily: '"Work Sans", Helvetica, Arial, sans-serif',
        order: ['title', 'location'],
      },
    };
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- Dev-only debug logging
      console.log('jobiqoWidgetConfig', widgetConfig);
    }
    jobiqoJobWidget.init(widgetConfig);

    const widgetEl = document.querySelector<HTMLElement>('#jobiqo_job_widget');
    const section = document.querySelector<HTMLElement>(`[data-jobiqo-widget="${config.widgetId}"]`);
    if (widgetEl && section) {
      hideOnEmpty(widgetEl, section);
    }
    if (widgetEl) {
      stripCountryInLocationStrings(widgetEl);
      fixLogoAltText(widgetEl, logoOrganizationMap);
      fixSliderForFewSlides(widgetEl);
    }
  };

  if (typeof jobiqoJobWidget !== 'undefined') {
    initWidget();
  } else {
    document.addEventListener('jobiqoWidgetLoaded', initWidget);
  }
}
